import React, { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { fmt, fmtDate } from '../utils';
import { Card, CardHeader, StatCard, StatGrid, Tag, SearchInput, Skeleton, PageWrap, PageHeader, Table, useIsMobile } from '../components/UI';
import '../styles/Payables.css';


export default function Payables({ sourceId, syncRunId }) {
  const isMobile = useIsMobile();
  const { data, loading } = useApi(() => api.outstandingPay(sourceId, syncRunId), [sourceId, syncRunId]);
  const { data: grossData, loading: grossLoading } = useApi(
    () => api.grossPayables(sourceId, syncRunId),
    [sourceId, syncRunId]
  );
  const [search, setSearch] = useState('');
  const [view,   setView]   = useState('net');

  const netRows = useMemo(() => {
    let r = data?.creditors || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x => x.name?.toLowerCase().includes(q));
    }
    return r;
  }, [data, search]);

  const billRows = useMemo(() => {
    let r = grossData?.bills || [];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(x => x.party_name?.toLowerCase().includes(q));
    }
    return r;
  }, [grossData, search]);

  const total     = data?.totalOutstanding ?? 0;
  const within30  = (data?.creditors || []).reduce((s, r) => s + (parseFloat(r.within_30)  || 0), 0);
  const days31_60 = (data?.creditors || []).reduce((s, r) => s + (parseFloat(r.days_31_60) || 0), 0);
  const days61_90 = (data?.creditors || []).reduce((s, r) => s + (parseFloat(r.days_61_90) || 0), 0);
  const above90   = (data?.creditors || []).reduce((s, r) => s + (parseFloat(r.above_90)   || 0), 0);

  const grossTotal = useMemo(() =>
    (grossData?.bills || []).reduce((s, b) => s + (parseFloat(b.pending_amount) || 0), 0),
  [grossData]);
  const grossBillCount = grossData?.bills?.length ?? 0;

  const bucketColor = (days) => {
    if (days == null) return 'var(--text3)';
    if (days <= 30)   return 'var(--green, #22c55e)';
    if (days <= 60)   return 'var(--yellow, orange)';
    if (days <= 90)   return 'orange';
    return 'var(--red)';
  };

  const bucketBg = (bucket) => {
    if (bucket === '0-30')  return { bg: '#dcfce7', fg: '#15803d' };
    if (bucket === '31-60') return { bg: '#fef9c3', fg: '#854d0e' };
    if (bucket === '61-90') return { bg: '#ffedd5', fg: '#9a3412' };
    return { bg: '#fee2e2', fg: '#991b1b' };
  };

  return (
    <PageWrap>
      <PageHeader title="Payables" sub="Outstanding creditor balances with FIFO ageing" />

      <StatGrid cols={isMobile ? 2 : 5}>
        <StatCard label="Net Outstanding" value={`₹${fmt(total)}`}     accent="red" />
        <StatCard label="0–30 Days"        value={`₹${fmt(within30)}`} />
        <StatCard label="31–60 Days"       value={`₹${fmt(days31_60)}`} accent={days31_60 > 0 ? 'yellow' : undefined} />
        <StatCard label="61–90 Days"       value={`₹${fmt(days61_90)}`} accent={days61_90 > 0 ? 'orange' : undefined} />
        <StatCard label="> 90 Days"        value={`₹${fmt(above90)}`}   accent={above90  > 0 ? 'red'    : undefined} />
      </StatGrid>

      {grossTotal > 0 && (
        <div className="pay-gross-callout">
          <span>📋</span>
          <span>
            Gross pending bills (FIFO, before payment netting):&nbsp;
            <strong className="pay-callout-accent">₹{fmt(grossTotal)}</strong>
            &nbsp;across&nbsp;
            <strong className="pay-callout-accent">{grossBillCount} bills</strong>
            &nbsp;·&nbsp;
            <span className="pay-callout-muted">Net outstanding after payments: ₹{fmt(total)}</span>
          </span>
        </div>
      )}

      <Card>
        <CardHeader
          title={view === 'net' ? `${netRows.length} Creditors` : `${billRows.length} Pending Bills`}
          right={
            <div className="pay-header-right">
              <SearchInput value={search} onChange={setSearch} placeholder="Search party..." />
              <div className="pay-toggle-wrap">
                <button
                  className={`pay-toggle-btn${view === 'net' ? ' pay-toggle-btn--active' : ''}`}
                  onClick={() => setView('net')}
                >
                  Net View
                </button>
                <button
                  className={`pay-toggle-btn${view === 'bill' ? ' pay-toggle-btn--active' : ''}`}
                  onClick={() => setView('bill')}
                >
                  Bill View
                </button>
              </div>
            </div>
          }
        />

        {view === 'net' && (
          loading ? <Skeleton rows={8} /> : (
            <Table
              columns={[
                { label: '#', key: '_idx',
                  render: r => <span className="pay-dim">{r._idx}</span> },
                { label: 'Party Name', key: 'name' },
                { label: 'GSTIN', key: 'gstin',
                  render: r => r.gstin ? <Tag>{r.gstin}</Tag> : <span className="pay-dim">—</span> },
                { label: 'Group', key: 'raw_group',
                  render: r => <span className="pay-dim-sm">{r.raw_group}</span> },
                { label: 'Total (₹)', key: 'total_outstanding', right: true, mono: true,
                  render: r => <span className="pay-red-bold">{fmt(r.total_outstanding)}</span> },
                { label: '0–30d', key: 'within_30', right: true, mono: true,
                  render: r => parseFloat(r.within_30) > 0
                    ? <span>{fmt(r.within_30)}</span>
                    : <span className="pay-dim">—</span> },
                { label: '31–60d', key: 'days_31_60', right: true, mono: true,
                  render: r => parseFloat(r.days_31_60) > 0
                    ? <span className="pay-yellow">{fmt(r.days_31_60)}</span>
                    : <span className="pay-dim">—</span> },
                { label: '61–90d', key: 'days_61_90', right: true, mono: true,
                  render: r => parseFloat(r.days_61_90) > 0
                    ? <span className="pay-orange">{fmt(r.days_61_90)}</span>
                    : <span className="pay-dim">—</span> },
                { label: '> 90d', key: 'above_90', right: true, mono: true,
                  render: r => parseFloat(r.above_90) > 0
                    ? <span className="pay-red-bold">{fmt(r.above_90)}</span>
                    : <span className="pay-dim">—</span> },
                { label: 'Oldest Invoice', key: 'oldest_invoice_date', noWrap: true,
                  render: r => r.oldest_invoice_date
                    ? <span className="pay-date-sm">{fmtDate(r.oldest_invoice_date)}</span>
                    : <span className="pay-dim">—</span> },
              ]}
              rows={netRows.map((r, i) => ({ ...r, _idx: i + 1 }))}
              emptyMsg="No outstanding payables"
            />
          )
        )}

        {view === 'bill' && (
          grossLoading ? <Skeleton rows={8} /> : (
            <>
              <div className="pay-bill-bar">
                <span>
                  Individual pending bills — oldest invoices assumed paid first (FIFO).
                  Parties with no invoices shown as balance-only rows.
                </span>
                <strong className="pay-bill-total">Total: ₹{fmt(grossTotal)}</strong>
              </div>
              <Table
                columns={[
                  { label: '#', key: '_idx',
                    render: r => <span className="pay-dim">{r._idx}</span> },
                  { label: 'Date', key: 'bill_date', noWrap: true,
                    render: r => r.bill_date
                      ? <span className="pay-date">{fmtDate(r.bill_date)}</span>
                      : <span className="pay-dim">—</span> },
                  { label: 'Voucher', key: 'voucher_number',
                    render: r => r.voucher_number ? <Tag>{r.voucher_number}</Tag> : <span className="pay-dim">—</span> },
                  { label: 'Party Name', key: 'party_name' },
                  { label: 'Group', key: 'raw_group',
                    render: r => <span className="pay-dim-sm">{r.raw_group}</span> },
                  { label: 'Narration', key: 'narration',
                    render: r => r.narration
                      ? <span className="pay-narration">
                          {r.narration.slice(0, 40)}{r.narration.length > 40 ? '…' : ''}
                        </span>
                      : <span className="pay-dim">—</span> },
                  { label: 'Pending (₹)', key: 'pending_amount', right: true, mono: true,
                    render: r => <span className="pay-red-bold">{fmt(r.pending_amount)}</span> },
                  { label: 'Age', key: 'days_overdue', right: true,
                    render: r => r.days_overdue != null
                      ? <span className="pay-age" style={{ color: bucketColor(r.days_overdue) }}>
                          {r.days_overdue}d
                        </span>
                      : <span className="pay-dim">—</span> },
                  { label: 'Bucket', key: 'age_bucket',
                    render: r => {
                      const { bg, fg } = bucketBg(r.age_bucket);
                      return (
                        <span className="pay-bucket" style={{ background: bg, color: fg }}>
                          {r.age_bucket}
                        </span>
                      );
                    }},
                ]}
                rows={billRows.map((r, i) => ({ ...r, _idx: i + 1 }))}
                emptyMsg="No pending bills"
              />
            </>
          )
        )}
      </Card>
    </PageWrap>
  );
}
